import type { FC, ReactNode } from 'react'

import { AlertTriangle, CheckCircle, Info } from 'lucide-react'

import { cn } from '@/lib/utils'

import { SmartButton } from '@/components/ui-custom/SmartButton'

type Variant = 'info' | 'warning' | 'error' | 'success'

const variantMap: Record<Variant, { icon: FC<{ className?: string }>; color: string; bg: string }> = {
  info: {
    icon: Info,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-400/20',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-yellow-600 dark:text-yellow-400',
    bg: 'bg-yellow-100 dark:bg-yellow-400/20',
  },
  error: {
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-400/20',
  },
  success: {
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-400/20',
  },
}

interface SystemMessageProps {
  variant?: Variant
  title: string
  message: string
  action?: {
    label: string
    onClick: () => void
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  children?: ReactNode
  className?: string
}

export const SystemMessage: FC<SystemMessageProps> = ({
  variant = 'info',
  title,
  message,
  action,
  secondaryAction,
  children,
  className,
}) => {
  const { icon: Icon, color, bg } = variantMap[variant]

  return (
    <div
      className={cn(
        'animate-fade-in mx-auto w-full max-w-lg space-y-4 rounded-xl border border-gray-100 bg-white px-8 py-12 text-center shadow-sm sm:rounded-2xl',
        className
      )}
    >
      <div
        className={cn(
          'mx-auto flex h-14 w-14 shrink-0 items-center justify-center rounded-lg ring-1 transition-transform duration-200 hover:scale-105',
          bg,
          color,
          variant === 'info' && 'ring-blue-200',
          variant === 'success' && 'ring-green-200',
          variant === 'warning' && 'ring-yellow-200',
          variant === 'error' && 'ring-red-200'
        )}
      >
        <Icon className='h-6 w-6' />
      </div>

      <h1
        className={cn(
          'text-foreground text-2xl font-semibold tracking-tight',
          variant === 'info' && 'text-blue-600',
          variant === 'success' && 'text-green-600',
          variant === 'warning' && 'text-yellow-600',
          variant === 'error' && 'text-red-600'
        )}
      >
        {title}
      </h1>

      <p className='text-sm leading-relaxed text-gray-500'>{message}</p>

      {children}
      <div className='flex justify-center gap-3'>
        {action && (
          <SmartButton variant={'default'} onClick={action.onClick} className='sm:w-auto'>
            {action.label}
          </SmartButton>
        )}
        {secondaryAction && (
          <SmartButton variant={'outline'} onClick={secondaryAction.onClick} className='sm:w-auto'>
            {secondaryAction.label}
          </SmartButton>
        )}
      </div>
    </div>
  )
}
